/**
 * Push reminders (docs/NOTIFICATIONS.md; owner decision 2026-09-29 "Both": push for app and browser users, one-tap text
 * through Hit Em Up for everyone else). This file is the ONE pure core that decides what is sent, to whom, and when.
 * The API's dispatcher (services/api/src/push/dispatch.ts) only gathers the inputs, sends, and records the outcome.
 *
 * | Rule | Why |
 * |---|---|
 * | P1 Seven kinds only: a check due (critical or not), a report that needs the person in charge, a shift hand-off, a Nag, an Ask answer, a task given to you, an order-list alert for managers | A rush leaves room for few interruptions; each one must be something to DO (ONTOLOGICAL_CENTER §2.5: the right person gets the one action) |
 * | P2 Words are fixed templates: the action only, never a symptom, never a note typed by someone (FRAMEWORK T4, S2 privacy) | A lock screen is public. "A team member can't do food work today" tells the person in charge what to do, not why |
 * | P3 Only a check's own label is ever filled in, and it is dropped when it holds any health word (`HEALTH_WORDS`) | An owner can name an area anything; the scan is the last gate, and every template is scanned by a test |
 * | P4 Each person gets only their own items: a check reaches the people on today's shift; a report or hand-off reaches its recipients; an Ask answer reaches the asker | Staff never learn about another person's report from a push |
 * | P4b An owner, admin or manager with no set work days at a place counts as on shift there for its check reminders (`reminderLeadUserIds`); a cooling batch still reaches only the shift and the cook | Owner, 2026-10-08: the owner who runs the place unscheduled was on nobody's shift, so no reminder ever reached them (push_sends was empty on staging) |
 * | P5 Consent per channel (phone, browser) and per kind. Default: critical checks and reports ON, a task someone gave you (`assigned`) and the managers' order list (`order_list`) ON, the rest OFF (opt-in). The last two are a direct ask and the manager's own list (owner, 2026-10-01: "default on for managers"); each person can turn them off | Respectful by default: safety-critical reminders insist, everything else is the person's choice (G2b) |
 * | P6 Quiet hours: nothing is sent outside the location's hours, give or take an hour for openers and closers (hours.ts). A closed day is quiet all day. Held items go out when the quiet ends, if still true | Never at night. The hour on each side is a judgment default (ASSUMED), not a legal number |
 * | P7 Rate limit: at most one push per item per person per `RATE_WINDOW_MIN`, and a non-critical push waits when the person had `BURST_MAX` pushes in the last `BURST_WINDOW_MIN` | "No more than one reminder per check per person per window" (owner brief); few, timely, actionable |
 * | P7b Several checks due for one person at the same time go out as ONE push ("2 more checks are open too"), the most important named | At 10:00 a café has the fridge check and the TCS temperatures due together; two buzzes mid-rush for one walk to the fridge is noise |
 * | P8 Next-card priorities (next.ts N1/N2): a critical check (CRITICAL_TEMPLATES) reminds when due and insists while it's late (up to `CRITICAL_MAX_SENDS`); a non-critical check waits until its late time and reminds once | "Critical items insist and others wait" (owner brief). Food Code 3-501.16(A)(2), 4-501.114 make holding and sanitizer checks unable to wait |
 * | P9 Idempotent: every send has a stable key (kind, target, stage, person, device); the ledger claims the key before sending | A restart or a second server never sends twice |
 * | P10 An item expires (default 12 h, a check at the end of its day); an expired item is never sent late | A reminder about the morning's walk-in check is noise at 9 PM |
 * | P12 A task given to someone climbs a ladder: at once the Today card and a push (phone, browser; never email at this step unless they chose email); then, if they haven't opened it, tapped Got it or done it after `escalateAfterMin` (15 min when it is due within 2 hours, else 60), one email (reach.ts R9). Quiet hours hold that email unless it is a food-safety check and the business lets those through (R5) | Owner, 2026-10-08: "in app … over the air and if they don't respond, for now an email" |
 *
 * The permission ask (P11) is here too: only after a value moment, never at first launch (B = MAT: ask at peak
 * motivation, one tap, never wall safety).
 */
import { CRITICAL_TEMPLATES } from "./next.ts";
import { COOLING } from "./cooling.ts";
import { addLocalDays, hoursOn, localDateMinutes, spanMinutes, type HoursSource } from "./hours.ts";
import type { Id, IsoDateTime } from "./model.ts";
import { firstName } from "./actions.ts";
import type { EmergencyId } from "./notifyprefs.ts";
import { GUEST_AREAS, GUEST_PUSH, parseGuestRef } from "./guestnote-lite.ts";
import { DEFAULT_REACH, DEFAULT_REACH_POLICY, REACH_FALLBACK, ignoresQuiet, reachKindOf, type ReachChannel, type ReachKind, type ReachPolicy, type ReachPrefs } from "./reach.ts";

/* ─────────────── kinds, channels, consent ─────────────── */

export type PushType = "critical_check" | "check" | "report" | "handoff" | "nag" | "ask_answer" | "assigned" | "order_list";
export type PushChannel = "phone" | "browser";
export const PUSH_CHANNELS: readonly PushChannel[] = ["phone", "browser"];

export interface PushPrefs {
  channels: Record<PushChannel, boolean>;
  types: Record<PushType, boolean>;
}

/** P5: safety-critical on, the rest opt-in. A channel only ever sends to a device the person allowed on that device. */
export const DEFAULT_PUSH_PREFS: PushPrefs = {
  channels: { phone: true, browser: true },
  // `assigned` and `order_list` are a direct ask from a manager and the manager's own list: on (delegate.ts, orderalert.ts), still the person's choice.
  types: { critical_check: true, report: true, check: false, handoff: false, nag: false, ask_answer: false, assigned: true, order_list: true },
};

/** What Profile lists, in this order. `critical` kinds are the ones on by default. */
export const PUSH_TYPES: readonly { type: PushType; label: string; detail: string; critical: boolean }[] = [
  { type: "critical_check", label: "Food safety checks", detail: "Fridge, hot holding, cooling and sanitizer checks, when they're due.", critical: true },
  { type: "report", label: "A report needs you", detail: "When you're in charge and someone reports a problem.", critical: true },
  { type: "check", label: "Other checks", detail: "Cleaning and other checks, only if they're running late.", critical: false },
  { type: "handoff", label: "Shift hand-offs", detail: "When someone hands you the shift.", critical: false },
  { type: "nag", label: "Nudges from your manager", detail: "When your manager asks you to do something.", critical: false },
  { type: "ask_answer", label: "Answers to your questions", detail: "When your owner, or CleanCleaver from its guides, answers a question you asked.", critical: false },
  { type: "assigned", label: "Tasks given to you", detail: "When someone gives you a task, and when it is due.", critical: false },
  { type: "order_list", label: "Order list", detail: "When something is added to the order list. For managers.", critical: false },
];

export const PUSH_TYPE_IDS: readonly PushType[] = PUSH_TYPES.map((t) => t.type);

/** Merges stored prefs over the defaults (a missing key keeps its default). Unknown keys are ignored. */
export function pushPrefsOf(stored?: Partial<{ channels: Partial<Record<string, unknown>>; types: Partial<Record<string, unknown>> }> | null): PushPrefs {
  const out: PushPrefs = { channels: { ...DEFAULT_PUSH_PREFS.channels }, types: { ...DEFAULT_PUSH_PREFS.types } };
  for (const c of PUSH_CHANNELS) if (typeof stored?.channels?.[c] === "boolean") out.channels[c] = stored.channels[c] as boolean;
  for (const t of PUSH_TYPE_IDS) if (typeof stored?.types?.[t] === "boolean") out.types[t] = stored.types[t] as boolean;
  return out;
}

/** A registered device as its owner sees it (GET /me/push). Never the token. */
export interface PushDeviceView { id: Id; channel: PushChannel; platform: string | null; createdAt: IsoDateTime }

/** GET /me/push: what this server can send, my choices, my devices, and the kinds to list in Profile. */
export interface PushSettingsView {
  /**
   * What this server can send. `phoneProviders` (2026-10-09, no EAS): which phone push services are set up here: the
   * Expo push service (always), Apple's own (APNs: APNS_* secrets) and Firebase Cloud Messaging (FCM_SERVICE_ACCOUNT_JSON).
   * An older server leaves it out; the phone app then treats Apple and Firebase as not set up.
   */
  config: { phone: boolean; phoneProviders?: Record<PhonePushProvider, boolean>; browser: { available: boolean; publicKey?: string } };
  prefs: PushPrefs;
  devices: PushDeviceView[];
  types: typeof PUSH_TYPES;
}

/**
 * How a phone token is delivered (2026-10-09, owner: "I dont have an EAS account. I use Xcode typically"):
 * - "expo": an Expo push token (ExponentPushToken[…]), sent through the Expo push service (needs an EAS project);
 * - "apns": the iPhone's own APNs device token (hex), sent straight to Apple (services/api/src/push/apns.ts);
 * - "fcm": the Android phone's own FCM registration token, sent straight to Firebase (services/api/src/push/fcm.ts).
 */
export type PhonePushProvider = "expo" | "apns" | "fcm";
export const PHONE_PUSH_PROVIDERS: readonly PhonePushProvider[] = ["expo", "apns", "fcm"];
/** APNs has two servers: development (sandbox: Xcode and dev builds) and production (TestFlight and the App Store). */
export type ApnsEnv = "development" | "production";
export const APNS_ENVS: readonly ApnsEnv[] = ["development", "production"];

/** POST /me/push/devices. A phone token without `provider` is an Expo token (every app build before 2026-10-09). */
export type PushDeviceRegistration =
  | { channel: "phone"; token: string; platform?: string; provider?: PhonePushProvider; apnsEnv?: ApnsEnv; appId?: string }
  | { channel: "browser"; subscription: { endpoint: string; keys: { p256dh: string; auth: string } }; platform?: string };

/** PUT /me/push/prefs: only what changes. */
export interface PushPrefsPatch { channels?: Partial<Record<PushChannel, boolean>>; types?: Partial<Record<PushType, boolean>> }

/* ─────────────── words (P2, P3) ─────────────── */

/**
 * Health words that must never be on a lock screen (FRAMEWORK T4, STANDARDS S8). Broader than askfeedback.ts SYMPTOM:
 * this one also covers the Food Code's reportable illnesses (2-201.11(A)(2): norovirus, hepatitis A, Shigella, STEC,
 * typhoid, nontyphoidal Salmonella) and the conditions 2-201.11(A)(1) lists (jaundice, a sore throat with fever,
 * an infected wound or boil).
 */
export const HEALTH_WORDS = /\b(sick\w*|ill|illness\w*|unwell|symptom\w*|vomit\w*|throw(?:ing|n)? up|threw up|diarrh\w*|fever\w*|nause\w*|cough\w*|sore throat|rash\w*|stomach|jaundice\w*|norovirus|noro|hepatitis|hep a|salmonell\w*|shigell\w*|e\.? ?coli|stec|typhoid\w*|infect\w*|wound\w*|boil|boils|lesion\w*|pus|flu|covid\w*|diagnos\w*|pregnan\w*|exposed|exposure|contagio\w*|disease\w*|medical|doctor|hospital\w*)\b/i;

export const hasHealthWords = (s: string): boolean => HEALTH_WORDS.test(s);

/** A check's label as a push may show it: short, one line, and never with a health word (P3). Undefined: use no label. */
export function safePushLabel(label: string | undefined): string | undefined {
  const t = (label ?? "").replace(/\s+/g, " ").trim().replace(/[.:]+$/, "");
  if (!t || hasHealthWords(t)) return undefined;
  // "Walk-in cooler" → "walk-in cooler", but an acronym keeps its capitals ("TCS item temperatures").
  const lower = /^[A-Z][a-z]/.test(t) ? t.charAt(0).toLowerCase() + t.slice(1) : t;
  return lower.length > 48 ? `${lower.slice(0, 47).trimEnd()}…` : lower;
}

/** Where a tap goes: a web path under the app, and a phone route (expo-router). The check form opens directly. */
export interface PushLink { web: string; phone: string }

export interface PushMessage { title: string; body: string; link: PushLink }

/** What a push is about. Built by the dispatcher from real rows (notices, the day's expected checks, Ask answers). */
export interface PushEvent {
  type: PushType;
  /** Stable per item: `check:<location>:<date>:<key>`, `notice:<id>`, `ask:<id>`. */
  target: string;
  /** due / late / insist-N for checks; once for everything else. Part of the idempotency key (P9). */
  stage: string;
  orgId: Id;
  locationId?: Id;
  /** The sign-in user who receives it (P4). */
  userId: Id;
  /** When it became true. */
  at: IsoDateTime;
  /** P10: never sent after this. */
  expiresAt: IsoDateTime;
  critical: boolean;
  /**
   * A clock a person started (a cooling batch, core cooling.ts): quiet hours (P6) don't hold it. Food cooled after close
   * still has to reach 70°F by 2 hours and 41°F by 6 in all, and the cook asked to be told.
   */
  timer?: true;
  /** P12: the email step of an unopened task. Goes only by email, only with R9 on, once (its own ledger key); not held by the per-item window or the burst cap. */
  escalate?: true;
  /** notifyprefs.ts N11: this event is an emergency (a late recheck of a critical reading, a sick or fire report). */
  emergency?: EmergencyId;
  /** What the words may name (never free text from a person). */
  subject:
    | { kind: "check"; key: string; label: string; late: boolean }
    | { kind: "report"; incidentId?: string; private: boolean }
    | { kind: "place_alert" }
    | { kind: "staffing" }
    | { kind: "handoff" }
    | { kind: "nag"; checkKey?: string; label?: string }
    | { kind: "ask_answer"; askId: string }
    | { kind: "link_fixed" }
    | { kind: "assigned"; key?: string; label?: string; due?: boolean }
    | { kind: "order_list" }
    // A guest's private note (guestnote.ts G4): the area only (our own fixed word), never the note.
    | { kind: "guest_note"; area: import("./guestnote-lite.ts").GuestArea; noteId?: string }
    // A teammate on a step asked for help (helprequest.ts). The pieces are our own words (a guide's title, its step); each passes the health scan (H4).
    | { kind: "help"; place?: string; step?: number; of?: number; known: boolean; private: boolean; link: PushLink };
}

const checkLink = (key: string): PushLink => ({ web: `/do?check=${encodeURIComponent(key)}`, phone: `/today?check=${encodeURIComponent(key)}` });

/** The words. Fixed templates; the only filled-in part is a check label that passed `safePushLabel` (P2, P3). */
export function pushMessage(e: Pick<PushEvent, "subject">): PushMessage {
  const s = e.subject;
  switch (s.kind) {
    case "check": {
      const label = safePushLabel(s.label);
      if (s.late) return { title: label ? `Still open: ${label}` : "A check is still open", body: "Tap to log it now.", link: checkLink(s.key) };
      return { title: label ? `A check is due: ${label}` : "A check is due", body: "Tap to log it now.", link: checkLink(s.key) };
    }
    case "report":
      // The sick-call morning: the action, never the reason (T4). "The plan" is the report's Do-this-first steps.
      if (s.private) return { title: "A team member can't do food work today.", body: "Open the plan.", link: reportLink(s.incidentId) };
      return { title: "A report needs you.", body: "Open it to see what to do first.", link: reportLink(s.incidentId) };
    case "staffing":
      // A sick answer in the daily check-in (notifyprefs.ts N11): a staffing need, with no name and no condition (T4).
      return { title: "Someone on today's shift can't work.", body: "Open the app to see who and arrange cover.", link: { web: "/today", phone: "/today" } };
    case "place_alert":
      return { title: "Something at your place needs a fix.", body: "Open it to see what to do.", link: { web: "/health", phone: "/health" } };
    case "handoff":
      return { title: "You have the shift now.", body: "Open Today to see what's still open.", link: { web: "/today", phone: "/today" } };
    case "nag": {
      const label = s.checkKey ? safePushLabel(s.label) : undefined;
      if (s.checkKey) return { title: label ? `Can you do this now: ${label}?` : "Can you do a check now?", body: "Tap to open it.", link: checkLink(s.checkKey) };
      return { title: "Your manager asked for your help.", body: "Open Today to see what.", link: { web: "/today", phone: "/today" } };
    }
    case "ask_answer":
      return { title: "Your question has an answer.", body: "Tap to read it.", link: { web: "/", phone: "/today" } };
    case "link_fixed":
      // A link the person reported ("Link not working?", core linkreports.ts) is fixed. Reuses the Ask-answer kind and its choice.
      return { title: "The link you reported is fixed.", body: "Thanks for telling us.", link: { web: "/", phone: "/today" } };
    case "assigned": {
      // A task given to this person (delegate.ts). Never the asker's name (P2): the lock screen is public. The label passes the same scan as a check's.
      const label = s.key ? safePushLabel(s.label) : undefined;
      const link = s.key ? checkLink(s.key) : { web: "/today", phone: "/today" };
      if (s.due) return { title: label ? `Your task is due: ${label}` : "A task you were given is due", body: "Tap to do it now.", link };
      return { title: label ? `A task is yours: ${label}` : "A task is yours", body: "Tap to see it.", link };
    }
    case "help": {
      // R19-01, P2: outside the app (lock screen, email) only fixed words and, for a guide the server knows, its own check title and step number.
      // A person's own words and the asker's name stay inside the app. A guide about illness says only "a check-in".
      const link = s.private ? { web: "/today", phone: "/today" } : s.link;
      if (s.private) return { title: "A teammate needs help with a check-in", body: "Open Today to see who.", link };
      const place = s.known ? safePushLabel(s.place) : undefined;
      if (!place) return { title: "A teammate needs help", body: "Open Today to see where.", link };
      return { title: "A teammate needs help", body: `${place.charAt(0).toUpperCase()}${place.slice(1)}${s.step && s.of ? `, step ${s.step} of ${s.of}` : ""}`, link };
    }
    case "guest_note":
      // G4: only that a guest left a note, and the area's fixed words. Never the note, never an illness word (T4).
      return { title: GUEST_PUSH.title(s.area), body: GUEST_PUSH.body, link: { web: s.noteId ? `/guest-notes?note=${encodeURIComponent(s.noteId)}` : "/guest-notes", phone: "/today" } };
    case "order_list":
      // Managers only. Never the item or who added it (P2): those are in the app, where it can be thanked.
      return { title: "Something was added to the order list.", body: "Open it to see what, and to say thanks.", link: { web: "/order", phone: "/order" } };
  }
}

function reportLink(id?: string): PushLink {
  return id ? { web: `/report#${encodeURIComponent(id)}`, phone: `/today?report=${encodeURIComponent(id)}` } : { web: "/report", phone: "/report" };
}

/**
 * Every template the core can produce, with a label full of health words to prove P3 strips it. The payload scan test
 * runs `hasHealthWords` over each; the metric "payload health-word scan = 0" is this list.
 */
export function everyPushTemplate(label = "Walk-in cooler"): PushMessage[] {
  const subjects: PushEvent["subject"][] = [
    { kind: "check", key: "k", label, late: false }, { kind: "check", key: "k", label, late: true },
    { kind: "report", incidentId: "i", private: true }, { kind: "report", incidentId: "i", private: false }, { kind: "report", private: true },
    ...GUEST_AREAS.map((a): PushEvent["subject"] => ({ kind: "guest_note", area: a.id, noteId: "n" })),
    { kind: "place_alert" }, { kind: "staffing" }, { kind: "handoff" },
    { kind: "nag", checkKey: "k", label }, { kind: "nag" },
    { kind: "ask_answer", askId: "a" }, { kind: "link_fixed" },
    { kind: "assigned", key: "k", label }, { kind: "assigned", key: "k", label, due: true }, { kind: "assigned" }, { kind: "order_list" },
    { kind: "help", place: label, step: 4, of: 6, known: true, private: false, link: { web: "/today", phone: "/today" } },
    { kind: "help", place: label, step: 4, of: 6, known: false, private: false, link: { web: "/today", phone: "/today" } },
    { kind: "help", place: label, step: 4, of: 6, known: true, private: true, link: { web: "/today", phone: "/today" } },
  ];
  return subjects.map((subject) => pushMessage({ subject }));
}

/* ─────────────── quiet hours (P6) ─────────────── */

/** Minutes either side of the location's hours when pushes may still go out (openers and closers). ASSUMED default. */
export const QUIET_MARGIN_MIN = 60;

/**
 * True when `now` is outside the location's hours ± QUIET_MARGIN_MIN (hours.ts H1–H3, overnight spans included).
 * Yesterday is checked too, so 00:30 at a bar that closes at 01:00 is not quiet.
 */
export function isQuietHours(loc: HoursSource, tz: string, now: Date | IsoDateTime, margin = QUIET_MARGIN_MIN): boolean {
  const { date, min } = localDateMinutes(now, tz);
  for (const [d, offset] of [[addLocalDays(date, -1), -1440], [date, 0]] as const) {
    const h = hoursOn(loc, d);
    if (h === "closed") continue;
    const { openMin, closeMin } = spanMinutes(h);
    if (min >= offset + openMin - margin && min <= offset + closeMin + margin) return false;
  }
  return true;
}

/* ─────────────── the planner (P4–P10) ─────────────── */

/** P7: one push per item per person per window. */
export const RATE_WINDOW_MIN = 30;
/** P7: a non-critical push waits when the person already had this many in the burst window. */
export const BURST_MAX = 2;
export const BURST_WINDOW_MIN = 30;
/**
 * Fatigue cap (owner, 2026-10-10): at most this many different non-safety pushes to one person in 24 hours; the next waits (held as
 * "fatigue"). Food safety checks, incident reports, critical alerts, the unopened-task email and sign-in codes are never capped: only
 * nudges, hand-offs, answers, assignments and order lists.
 */
export const FATIGUE_MAX = 6;
export const FATIGUE_EXEMPT_TYPES: readonly PushType[] = ["critical_check", "check", "report"];
/** P8: a late critical check is re-sent at most this many times in all (the due one included). */
export const CRITICAL_MAX_SENDS = 3;

/**
 * Where a push can go: the two device channels, email (one address per person, a virtual device `email:<user>`) and
 * in-app (a record only, `in_app:<user>`; core reach.ts). Text messages are not a delivery channel (reach.ts R1).
 */
export type DeliveryChannel = PushChannel | "email" | "in_app";

export interface PushDevice { id: Id; userId: Id; channel: DeliveryChannel }

/** The reach channel a delivery channel carries. */
export const reachOf = (c: DeliveryChannel): ReachChannel => (c === "phone" ? "app" : c);

/** One row of the send ledger, as the planner reads it. */
/**
 * One row of the send ledger, as the planner reads it. retry: claimed and in flight, or waiting to be tried again at
 * `retryAt` (a lease: a server that dies mid-send leaves a row that is retried once the lease runs out). sent: done.
 * dead: the push service refused it for good, or it ran out of tries.
 */
export interface PushSendRecord { key: string; userId: Id; target: string; at: IsoDateTime; status: "retry" | "sent" | "dead"; retryAt?: IsoDateTime }

export interface PushPlanInput {
  now: IsoDateTime;
  events: PushEvent[];
  prefs: Record<Id, PushPrefs | undefined>;
  devices: PushDevice[];
  /** Recent ledger rows (at least the last day), every status. */
  ledger: PushSendRecord[];
  /** Quiet hours per location id (P6): true = quiet now. A location not listed is not quiet. */
  quiet: Record<Id, boolean>;
  /** How each person wants to be reached (core reach.ts). Absent or missing: the defaults, which are today's behavior. */
  reach?: Record<Id, ReachPrefs | undefined>;
  /** The business's two settings, by org id (reach.ts R5, R6). */
  policy?: Record<Id, ReachPolicy | undefined>;
  /** Per person: inside their own quiet hours now (reach.ts R5). The dispatcher works it out in the place's time zone. */
  personQuiet?: Record<Id, boolean>;
  /** Channels this server can't use right now (browser without VAPID keys, email without a sender): never chosen, fallen back from. */
  unavailable?: DeliveryChannel[];
  /** Overrides FATIGUE_MAX (tests). */
  fatigueMax?: number;
}

export interface PushSend {
  key: string; event: PushEvent; device: PushDevice; message: PushMessage;
  /** R4: the person's chosen channel was unavailable, so this one carries it. */
  fellBackFrom?: ReachChannel;
  /** R6: the business minimum added this phone or browser to what the person chose (no longer happens: notifyprefs.ts N10). */
  addedByPolicy?: true;
  /** notifyprefs.ts N11: only "emergencies only" sent it (the channel is off for this business otherwise). */
  emergencyOnly?: true;
  /** P7b: other checks due for this person in the same minute, carried by this one push ("2 more checks are open too"). Recorded as sent with it. */
  bundled: { key: string; event: PushEvent }[];
}

export type PushHoldReason = "type_off" | "no_device" | "quiet" | "person_quiet" | "rate" | "burst" | "expired" | "done" | "limit" | "email_off" | "fatigue";
export interface PushHeld { event: PushEvent; reason: PushHoldReason }

/** Something worth a line in the trail that isn't a send or a hold: the business minimum couldn't be met (reach.ts R6). */
export interface PushNote { event: PushEvent; note: "policy_unmet" }

export interface PushPlan { send: PushSend[]; held: PushHeld[]; notes: PushNote[] }

/** P9: the idempotency key. Stable for the same item, stage, person and device. */
export const pushKey = (e: Pick<PushEvent, "type" | "target" | "stage" | "userId">, deviceId: Id) => `${e.type}|${e.target}|${e.stage}|${e.userId}|${deviceId}`;

const minsBefore = (iso: IsoDateTime, m: number) => new Date(Date.parse(iso) - m * 60_000).toISOString();

/**
 * Decides what goes out now. Pure: the same inputs give the same plan, so it is tested without a server (T7).
 * Order: expired → consent (kind) → quiet → rate → burst → per device (channel consent, already sent).
 */
export function planPushes(p: PushPlanInput): PushPlan {
  const send: PushSend[] = [], held: PushHeld[] = [], notes: PushNote[] = [];
  const counted = (r: PushSendRecord) => r.status !== "dead";
  const rateSince = minsBefore(p.now, RATE_WINDOW_MIN), burstSince = minsBefore(p.now, BURST_WINDOW_MIN);
  // Pushes planned in this same run count toward the limits, so two events in one tick can't both slip through.
  const planned: { userId: Id; type: PushType; target: string; at: IsoDateTime }[] = [];
  const recent = (userId: Id, since: IsoDateTime, target?: string) =>
    p.ledger.filter((r) => counted(r) && r.userId === userId && r.at >= since && (!target || r.target === target)).length
    + planned.filter((x) => x.userId === userId && x.at >= since && (!target || x.target === target)).length;
  // Different non-safety items sent to the person in the last 24 hours (a phone and a browser for one item count once).
  const daySince = minsBefore(p.now, 24 * 60);
  const fatigued = (e: PushEvent) => {
    if (e.critical || e.escalate || FATIGUE_EXEMPT_TYPES.includes(e.type)) return false;
    const items = new Set<string>();
    for (const r of p.ledger) { const t = r.key.split("|")[0] as PushType; if (counted(r) && r.userId === e.userId && r.at >= daySince && !FATIGUE_EXEMPT_TYPES.includes(t)) items.add(`${t}|${r.target}`); }
    for (const x of planned) if (x.userId === e.userId && !FATIGUE_EXEMPT_TYPES.includes(x.type)) items.add(`${x.type}|${x.target}`);
    return !items.has(`${e.type}|${e.target}`) && items.size >= (p.fatigueMax ?? FATIGUE_MAX);
  };
  // Critical first (P8), then oldest first, so the burst cap spends itself on what matters.
  const events = [...p.events].sort((a, b) => Number(b.critical) - Number(a.critical) || a.at.localeCompare(b.at) || a.target.localeCompare(b.target));
  for (const e of events) {
    if (p.now > e.expiresAt) { held.push({ event: e, reason: "expired" }); continue; }
    if (e.at > p.now) continue; // not yet true
    const prefs = p.prefs[e.userId] ?? DEFAULT_PUSH_PREFS;
    if (!prefs.types[e.type]) { held.push({ event: e, reason: "type_off" }); continue; }
    if (e.locationId && p.quiet[e.locationId] && !e.timer) { held.push({ event: e, reason: "quiet" }); continue; }
    const policy = p.policy?.[e.orgId] ?? DEFAULT_REACH_POLICY;
    // R5: the person's own quiet hours hold everything except what the business lets through.
    if (p.personQuiet?.[e.userId] && !ignoresQuiet(e, policy)) { held.push({ event: e, reason: "person_quiet" }); continue; }
    const chosen = chooseDevices(e, p, prefs, policy);
    notes.push(...chosen.notes);
    const devices = chosen.devices;
    if (!devices.length) { held.push({ event: e, reason: chosen.hold ?? "no_device" }); continue; }
    const rowOf = (d: PushDevice) => p.ledger.find((r) => r.key === pushKey(e, d.id));
    // Not yet tried, or a retry whose wait (or lease) is over. The event is rebuilt from live data every tick, so a
    // check logged in the meantime stops its own retries.
    const fresh = devices.filter((d) => { const r = rowOf(d.device); return !r || (r.status === "retry" && !!r.retryAt && r.retryAt <= p.now); });
    if (!fresh.length) { held.push({ event: e, reason: "done" }); continue; }
    // A stage already started (a retry, or one device done and one new) finishes without counting against itself.
    const stageStarted = fresh.length < devices.length || fresh.some((d) => !!rowOf(d.device));
    // P12: the email step follows the push on purpose, so the per-item window doesn't hold it (the burst cap still does).
    if (!stageStarted && !e.escalate && recent(e.userId, rateSince, e.target) > 0) { held.push({ event: e, reason: "rate" }); continue; }
    // The burst cap limits buzzes; the email step is one email per task, ever (its own ledger key), so it isn't held by the phone's buzzes.
    if (!stageStarted && fatigued(e)) { held.push({ event: e, reason: "fatigue" }); continue; }
    if (!stageStarted && !e.critical && !e.escalate && recent(e.userId, burstSince) >= BURST_MAX) { held.push({ event: e, reason: "burst" }); continue; }
    const message = pushMessage(e);
    for (const d of fresh) send.push({ key: pushKey(e, d.device.id), event: e, device: d.device, message, bundled: [], ...(d.fellBackFrom ? { fellBackFrom: d.fellBackFrom } : {}), ...(d.addedByPolicy ? { addedByPolicy: true as const } : {}), ...(d.emergencyOnly ? { emergencyOnly: true as const } : {}) });
    planned.push({ userId: e.userId, type: e.type, target: e.target, at: p.now });
  }
  return { send: bundleChecks(send), held, notes };
}

interface Chosen { device: PushDevice; fellBackFrom?: ReachChannel; addedByPolicy?: boolean; emergencyOnly?: boolean }

/**
 * Which of the person's devices carry this event (reach.ts R3, R4, R6). Their chosen channels that are available; when
 * none of the chosen external ones is, the next available in REACH_FALLBACK (the trail records it); and, when the business
 * minimum for cooling is on, a phone or browser as well. Today's defaults (phone and browser) keep today's behavior.
 */
function chooseDevices(e: PushEvent, p: PushPlanInput, prefs: PushPrefs, policy: ReachPolicy): { devices: Chosen[]; notes: PushNote[]; hold?: PushHoldReason } {
  const reach = p.reach?.[e.userId] ?? DEFAULT_REACH;
  const kind: ReachKind = reachKindOf(e);
  const down = new Set(p.unavailable ?? []);
  if (e.escalate) {
    // P12, R9: email only. Off when the person turned it off, or chose "In the app only" for these; nothing more when
    // they already chose email for them (the first step was the email).
    const kindChoice = reach.kinds[kind];
    if (!reach.escalateEmail || (kindChoice.length === 1 && kindChoice[0] === "in_app")) return { devices: [], notes: [], hold: "email_off" };
    if (kindChoice.includes("email")) return { devices: [], notes: [], hold: "done" };
    const mail = down.has("email") ? [] : p.devices.filter((d) => d.userId === e.userId && d.channel === "email");
    return { devices: mail.map((device) => ({ device })), notes: [] };
  }
  // Me → Notifications N10: what the person chose, and nothing more. A late safety check they left "in the app only" waits in the app.
  const wanted: ReachChannel[] = reach.kinds[kind];
  const mine = p.devices.filter((d) => d.userId === e.userId);
  const inApp: PushDevice = { id: `in_app:${e.userId}`, userId: e.userId, channel: "in_app" };
  const avail = (c: ReachChannel): PushDevice[] => {
    switch (c) {
      case "app": return mine.filter((d) => d.channel === "phone" && prefs.channels.phone);
      case "browser": return down.has("browser") ? [] : mine.filter((d) => d.channel === "browser" && prefs.channels.browser);
      case "email": return down.has("email") ? [] : mine.filter((d) => d.channel === "email");
      case "in_app": return [inApp];
      case "sms": return []; // R1: there is no provider; a text is never a delivery
    }
  };
  const out = new Map<string, Chosen>();
  const add = (d: PushDevice, extra: Omit<Chosen, "device"> = {}) => { if (!out.has(d.id)) out.set(d.id, { device: d, ...extra }); };
  const external = wanted.filter((c) => c !== "in_app");
  for (const c of wanted) for (const d of avail(c)) add(d);
  // R4: phone push that can't reach the phone goes to the browser (and back). Never across to email, never from email to push (N10).
  const pushFamily = external.filter((c) => c !== "email");
  if (pushFamily.length && !pushFamily.some((c) => avail(c).length > 0)) {
    const next = REACH_FALLBACK.find((c) => !wanted.includes(c) && avail(c).length > 0);
    if (next) for (const d of avail(next)) add(d, { fellBackFrom: pushFamily[0]! });
  }
  // N11: emergencies only. The person switched this channel off for this business and then opted in to emergencies.
  if (e.emergency && reach.emergency) {
    if (reach.emergency.push) for (const d of [...avail("app"), ...avail("browser")]) add(d, { emergencyOnly: true });
    if (reach.emergency.email) for (const d of avail("email")) add(d, { emergencyOnly: true });
  }
  const notes: PushNote[] = [];
  // R6: the business asks that cooling alerts reach someone by phone or browser. Off means off (notifyprefs.ts N10): it never
  // adds a phone or browser the person left off; when this person gets none, the business's trail says so.
  if (kind === "cooling" && policy.coolingByPhoneOrBrowser) {
    const pb = [...out.values()].filter((c) => c.device.channel === "phone" || c.device.channel === "browser");
    if (!pb.length) notes.push({ event: e, note: "policy_unmet" });
  }
  return { devices: [...out.values()], notes };
}

/**
 * P7b: several checks due for one person at once (10:00: the fridge check and the TCS temperatures) become ONE push per
 * device: the most important one names itself, the body counts the rest, and the tap opens the first. Each bundled
 * check is still recorded in the ledger, so the rate limit holds for every one of them.
 */
function bundleChecks(sends: PushSend[]): PushSend[] {
  const out: PushSend[] = [];
  const lead = new Map<string, PushSend>();
  for (const s of sends) {
    if (s.event.subject.kind !== "check") { out.push(s); continue; }
    const k = `${s.event.userId}|${s.device.id}`;
    const first = lead.get(k);
    if (!first) { lead.set(k, s); out.push(s); continue; }
    first.bundled.push({ key: s.key, event: s.event });
  }
  for (const s of lead.values()) {
    const n = s.bundled.length;
    if (n) s.message = { ...s.message, body: `Tap to log it now. ${n} more ${n === 1 ? "check is" : "checks are"} open too.` };
  }
  return out;
}

/* ─────────────── check events (P8) ─────────────── */

export interface PushCheckInput {
  orgId: Id;
  locationId: Id;
  date: string;
  now: IsoDateTime;
  /** Today's expected checks with whether each is satisfied (schedule.ts expectedChecks + isSatisfied). */
  checks: {
    key: string; label: string; templateId: string; dueAt: IsoDateTime; lateAt: IsoDateTime; firstDay?: true; satisfied: boolean;
    /** A cooling batch's reading (schedule.ts ExpectedCheck.cooling), with the sign-in user who started the batch. */
    cooling?: { startedBy?: Id };
  }[];
  /** Sign-in users on today's shift at this location (DayContext.onShift mapped to userIds; no sign-in, no push). */
  onShiftUserIds: Id[];
  /** P4b: owners, admins and managers with no set work days at this place (`reminderLeadUserIds`). They get its check reminders too. */
  leadUserIds?: Id[];
  /** End of this location's day (for P10). */
  dayEnd: IsoDateTime;
  /**
   * Delegation (delegate.ts): check key to the sign-in users it was given to (a role already resolved to the people on shift
   * it fits), and when it was given. A check given to someone reminds THEM when it is due (P4: each person gets their own
   * items) unless it was given after that time (the notice of the assignment already said "do it now"); while a food-safety
   * check is late it reminds the whole shift as well ("needs a hand"). Absent or missing a key: the shift, as before.
   */
  assigned?: Record<string, { users: Id[]; since: IsoDateTime } | undefined>;
}

/**
 * The check reminders that are true now, one per on-shift person (P4). A critical check: "due" at its due time, then
 * "insist-N" every RATE_WINDOW_MIN once it's late, up to CRITICAL_MAX_SENDS in all. A non-critical check: one "late"
 * at its late time (it waits, like the Next card's N2 deferral). First-day checks are where the owner starts, never
 * a reminder (schedule.ts `firstDay`).
 */
export function checkPushEvents(c: PushCheckInput): PushEvent[] {
  const out: PushEvent[] = [];
  const shift = [...new Set([...c.onShiftUserIds, ...(c.leadUserIds ?? [])])];
  for (const k of c.checks) {
    if (k.cooling) { out.push(...coolingPushEvents(c, k)); continue; }
    if (k.satisfied || k.firstDay || k.dueAt > c.now) continue;
    const critical = CRITICAL_TEMPLATES.has(k.templateId);
    const late = c.now > k.lateAt;
    const given = c.assigned?.[k.key];
    const owners = given?.users.length ? given.users : undefined;
    let stage: string, at: IsoDateTime;
    // Given to someone and not critical: they are told when it is due (once). The shift is not (P4).
    if (owners && !critical && !late) {
      if (given!.since >= k.dueAt) continue; // given after it was due: the assignment's own notice was the reminder
      for (const userId of owners) out.push({ type: "assigned", target: `check:${c.locationId}:${c.date}:${k.key}`, stage: "due", orgId: c.orgId, locationId: c.locationId, userId, at: k.dueAt, expiresAt: c.dayEnd, critical: false, subject: { kind: "assigned", key: k.key, label: k.label, due: true } });
      continue;
    }
    if (critical) {
      if (!late) { stage = "due"; at = k.dueAt; }
      else {
        const n = Math.min(CRITICAL_MAX_SENDS - 1, 1 + Math.floor((Date.parse(c.now) - Date.parse(k.lateAt)) / (RATE_WINDOW_MIN * 60_000)));
        stage = `insist-${n}`; at = new Date(Date.parse(k.lateAt) + (n - 1) * RATE_WINDOW_MIN * 60_000).toISOString();
      }
    } else {
      if (!late) continue;
      stage = "late"; at = k.lateAt;
    }
    // Given to someone: them. A late food-safety check also reminds the shift: it needs a hand.
    const to = owners ? (critical && late ? [...new Set([...shift, ...owners])] : owners) : shift;
    for (const userId of to) {
      out.push({
        type: critical ? "critical_check" : "check", target: `check:${c.locationId}:${c.date}:${k.key}`, stage, orgId: c.orgId, locationId: c.locationId, userId, at,
        expiresAt: c.dayEnd, critical, subject: { kind: "check", key: k.key, label: k.label, late },
        // N11: a critical reading that was out of range, its recheck now late: nobody has shown it fixed.
        ...(critical && late && k.key.startsWith("recheck:") ? { emergency: "unfixed_reading" as const } : {}),
      });
    }
  }
  return out;
}

/**
 * A cooling batch's reading (core cooling.ts), pushed at the same four points the phone would ring locally: the heads-up
 * 15 minutes before each deadline ("warn", its own target so the rate limit never delays the deadline push), then the
 * deadline itself and the insists while it's late (the critical path above). This is the ONE path when the person has
 * phone push: the phone schedules its local notices only when the server says push won't reach it (GET /cooling
 * `pushed`, coolingPushCovers). Differences from a routine check, each because a person started this clock:
 * - `timer`: quiet hours don't hold it (a batch started at close runs past it);
 * - the person who started the batch gets it too, on shift or not;
 * - the target has no date, so a batch that crosses midnight keeps one ledger (never sent twice).
 */
function coolingPushEvents(c: PushCheckInput, k: PushCheckInput["checks"][number]): PushEvent[] {
  if (k.satisfied) return [];
  const warnAt = new Date(Date.parse(k.dueAt) - COOLING.warnMin * 60_000).toISOString();
  if (c.now < warnAt) return [];
  const users = coolingPushUsers(c.onShiftUserIds, k.cooling?.startedBy);
  const base = { orgId: c.orgId, locationId: c.locationId, critical: true, timer: true as const, expiresAt: c.dayEnd > k.lateAt ? c.dayEnd : new Date(Date.parse(k.lateAt) + CRITICAL_MAX_SENDS * RATE_WINDOW_MIN * 60_000).toISOString() };
  const target = `check:${c.locationId}:${k.key}`;
  if (c.now < k.dueAt) return users.map((userId) => ({ ...base, type: "critical_check" as const, target: `${target}:warn`, stage: "warn", userId, at: warnAt, subject: { kind: "check" as const, key: k.key, label: k.label, late: false } }));
  const late = c.now > k.lateAt;
  let stage = "due", at = k.dueAt;
  if (late) {
    const n = Math.min(CRITICAL_MAX_SENDS - 1, 1 + Math.floor((Date.parse(c.now) - Date.parse(k.lateAt)) / (RATE_WINDOW_MIN * 60_000)));
    stage = `insist-${n}`; at = new Date(Date.parse(k.lateAt) + (n - 1) * RATE_WINDOW_MIN * 60_000).toISOString();
  }
  return users.map((userId) => ({ ...base, type: "critical_check" as const, target, stage, userId, at, subject: { kind: "check" as const, key: k.key, label: k.label, late } }));
}

/**
 * P4b: the people who run a place without a set shift: owners, admins and managers whose membership covers the place and
 * whose person record (if any) is active there with no work days. Someone with work days follows their schedule (P4).
 */
export function reminderLeadUserIds(locationId: Id, members: readonly { userId: Id; role: string; locationIds: readonly Id[]; employeeId?: Id }[],
  employees: readonly { id: Id; active: boolean; locationIds: readonly Id[]; workDays?: readonly number[] }[]): Id[] {
  const out: Id[] = [];
  for (const m of members) {
    if (m.role !== "owner" && m.role !== "admin" && m.role !== "manager") continue;
    if (m.locationIds.length && !m.locationIds.includes(locationId)) continue;
    const e = m.employeeId ? employees.find((x) => x.id === m.employeeId) : undefined;
    if (e && (!e.active || (e.locationIds.length > 0 && !e.locationIds.includes(locationId)) || (e.workDays?.length ?? 0) > 0)) continue;
    out.push(m.userId);
  }
  return [...new Set(out)];
}

/** P12: a task due within this many minutes of being given is urgent. */
export const ESCALATE_URGENT_WITHIN_MIN = 120;
/** P12: an urgent task not opened after this many minutes is emailed. */
export const ESCALATE_URGENT_AFTER_MIN = 15;
/** P12: any other task not opened after this many minutes is emailed. */
export const ESCALATE_AFTER_MIN = 60;

/** P12: minutes from giving a task to its email, when it hasn't been opened or done. No due time: not urgent. */
export function escalateAfterMin(givenAt: IsoDateTime, dueAt?: IsoDateTime): number {
  return dueAt && Date.parse(dueAt) - Date.parse(givenAt) <= ESCALATE_URGENT_WITHIN_MIN * 60_000 ? ESCALATE_URGENT_AFTER_MIN : ESCALATE_AFTER_MIN;
}

/**
 * P12: the email step for a task given to someone (a ShiftNotice of kind "assign" that names a check). None when the
 * person responded: opened it (`seenAt`: Today showed it to them, or they tapped Got it) or did the task (`done`). A notice
 * with nothing to do (someone stepped in for you) has no check and never climbs.
 */
export function assignEscalationEvent(n: { id: Id; orgId: Id; locationId: Id; kind: string; refId?: string; createdAt: IsoDateTime; seenAt?: string },
  userId: Id, o: { dueAt?: IsoDateTime; label?: string; critical?: boolean; done?: boolean } = {}): PushEvent | null {
  if (n.kind !== "assign" || !n.refId?.startsWith("check:") || n.seenAt || o.done) return null;
  const key = n.refId.slice(6);
  const at = new Date(Date.parse(n.createdAt) + escalateAfterMin(n.createdAt, o.dueAt) * 60_000).toISOString();
  return {
    type: "assigned", target: `notice:${n.id}`, stage: "escalate", escalate: true, orgId: n.orgId, locationId: n.locationId, userId, at,
    expiresAt: new Date(Date.parse(n.createdAt) + NOTICE_TTL_HOURS * 3_600_000).toISOString(), critical: !!o.critical,
    subject: { kind: "assigned", key, ...(o.label ? { label: o.label } : {}) },
  };
}

/** Who a cooling batch's pushes reach: the people on shift, and the person who started it. */
export function coolingPushUsers(onShiftUserIds: readonly Id[], startedBy?: Id): Id[] {
  return [...new Set([...onShiftUserIds, ...(startedBy ? [startedBy] : [])])];
}

/**
 * Will push carry this person's cooling notices to their phone, so the phone must NOT also ring locally (one path per
 * checkpoint)? Only when all of it is true: the server's dispatcher runs, the place is a business (homes never push),
 * they have a phone registered and phone reminders on, critical checks on, and they are one of the batch's recipients.
 * Anything false: the phone's local notices are the fallback.
 */
export function coolingPushCovers(o: { dispatch: boolean; business: boolean; phoneDevices: number; prefs: PushPrefs; recipient: boolean; reach?: ReachPrefs; policy?: ReachPolicy }): boolean {
  if (!(o.dispatch && o.business && o.phoneDevices > 0 && o.prefs.channels.phone && o.prefs.types.critical_check && o.recipient)) return false;
  // "How should we reach you?" (reach.ts): push carries the notice to the phone only if the person chose the phone for cooling
  // (or the business minimum adds it), and their quiet hours won't hold it. Otherwise the phone's own notice stays the path.
  const reach = o.reach ?? DEFAULT_REACH, policy = o.policy ?? DEFAULT_REACH_POLICY;
  if (!reach.kinds.cooling.includes("app") && !policy.coolingByPhoneOrBrowser) return false;
  if (reach.quiet.on && !policy.safetyIgnoresQuiet) return false;
  return true;
}

/** How long a hand-off, report or answer stays worth a push (P10). */
export const NOTICE_TTL_HOURS = 12;

/** A ShiftNotice row (handoff.ts) as a push event. Nudges (quick-log questions) never push. */
export function noticePushEvent(n: { id: Id; orgId: Id; locationId: Id; kind: "report" | "shift" | "nag" | "nudge" | "health" | "assign" | "order"; refId?: string; createdAt: IsoDateTime },
  userId: Id, o: { reportPrivate?: boolean; checkLabel?: (key: string) => string | undefined; help?: { fromName: string; title: string; step: number; of: number; say: string; web: string; phone: string; private: boolean; known?: boolean } } = {}): PushEvent | null {
  const base = { target: `notice:${n.id}`, stage: "once", orgId: n.orgId, locationId: n.locationId, userId, at: n.createdAt, expiresAt: new Date(Date.parse(n.createdAt) + NOTICE_TTL_HOURS * 3_600_000).toISOString() };
  switch (n.kind) {
    // A sick answer in the daily check-in (today.ts, refId "checkin:stop"): a staffing need, an emergency (notifyprefs.ts N11).
    // A teammate asked for help on a step (helprequest.ts): its own kind of words, in the "From your manager" choices (reach.ts), never critical.
    case "report": if (parseGuestRef(n.refId)) {
        const g = parseGuestRef(n.refId)!;
        // A guest's private note (guestnote.ts): not critical, not an emergency (a person who chose emergencies only is not woken by it).
        return { ...base, type: "report", critical: false, subject: { kind: "guest_note", area: g.area, noteId: g.id } };
      }
      if (n.refId?.startsWith("help:")) {
        const h = o.help;
        return { ...base, type: "report", critical: false, subject: { kind: "help", ...(h ? { place: h.title, step: h.step, of: h.of } : {}), known: !!h?.known, private: h?.private ?? true, link: h ? { web: h.web, phone: h.phone } : { web: "/today", phone: "/today" } } };
      }
      if (n.refId === "checkin:stop") return { ...base, type: "report", critical: true, subject: { kind: "staffing" }, emergency: "staffing" };
      return { ...base, type: "report", critical: true, subject: { kind: "report", ...(n.refId && !n.refId.startsWith("checkin:") ? { incidentId: n.refId } : {}), private: o.reportPrivate !== false } };
    case "health": return { ...base, type: "report", critical: true, subject: { kind: "place_alert" } };
    case "shift": return { ...base, type: "handoff", critical: false, subject: { kind: "handoff" } };
    case "nag": {
      // refId "check:<key>" for a check nag (nag.ts item refs); anything else says only "asked for your help".
      const key = n.refId?.startsWith("check:") ? n.refId.slice(6) : undefined;
      const label = key ? o.checkLabel?.(key) : undefined;
      return { ...base, type: "nag", critical: false, subject: { kind: "nag", ...(key ? { checkKey: key } : {}), ...(label ? { label } : {}) } };
    }
    case "assign": {
      const key = n.refId?.startsWith("check:") ? n.refId.slice(6) : undefined;
      const label = key ? o.checkLabel?.(key) : undefined;
      return { ...base, type: "assigned", critical: false, subject: { kind: "assigned", ...(key ? { key } : {}), ...(label ? { label } : {}) } };
    }
    case "order": return { ...base, type: "order_list", critical: false, subject: { kind: "order_list" } };
    case "nudge": return null;
  }
}

/** An owner's answer to a question this person asked (askfeedback.ts). */
export function askAnswerPushEvent(a: { id: Id; orgId: Id; userId: Id; answeredAt: IsoDateTime }): PushEvent {
  return { type: "ask_answer", target: `ask:${a.id}`, stage: "once", orgId: a.orgId, userId: a.userId, at: a.answeredAt, expiresAt: new Date(Date.parse(a.answeredAt) + 48 * 3_600_000).toISOString(), critical: false, subject: { kind: "ask_answer", askId: a.id } };
}

/** The fix for a link the person reported: the same kind (and the same choice) as "your question has an answer". One per fix: the target carries the time. */
export function linkFixedPushEvent(a: { id: Id; orgId: Id; userId: Id; fixedAt: IsoDateTime }): PushEvent {
  return { type: "ask_answer", target: `link:${a.id}:${a.fixedAt}`, stage: "once", orgId: a.orgId, userId: a.userId, at: a.fixedAt, expiresAt: new Date(Date.parse(a.fixedAt) + 48 * 3_600_000).toISOString(), critical: false, subject: { kind: "link_fixed" } };
}

/* ─────────────── retry (dispatcher) ─────────────── */

/** Tries per send before it's marked dead. */
export const PUSH_MAX_ATTEMPTS = 5;
/** Exponential backoff with a cap: 30 s, 1 min, 2 min, 4 min… (Expo: "use exponential backoff" on 429 / 5xx). */
export function pushBackoffMs(attempt: number): number {
  return Math.min(30_000 * 2 ** Math.max(0, attempt - 1), 15 * 60_000);
}

/* ─────────────── the permission ask (P11) ─────────────── */

/** Something the person just got value from. Absent: no ask (never at first launch). */
export type PushValueMoment = "check_logged" | "report_sent" | "question_noted";

export type PushSupport = "granted" | "default" | "denied" | "unsupported" | "needs_install";

export interface PushAskInput {
  /** What this device can do: the OS/browser permission, or why not. */
  support: PushSupport;
  /** This device is already registered with the server. */
  registered: boolean;
  moment?: PushValueMoment;
  /** When "Skip reminders" was last tapped on this device. */
  dismissedAt?: IsoDateTime;
  now: IsoDateTime;
}

/** Days a "Skip reminders" is respected before the next value moment may ask again. */
export const PUSH_ASK_SNOOZE_DAYS = 7;

export interface PushAsk { show: boolean; title: string; body: string; button: string }

const ASK_WORDS: Record<PushValueMoment, { title: string; body: string }> = {
  check_logged: { title: "Want a nudge when the next check is due?", body: "One reminder when it's due. Never at night." },
  report_sent: { title: "Want to hear when a report needs you?", body: "Only when you're in charge. Never at night." },
  question_noted: { title: "Want to hear when your question is answered?", body: "One message when the answer is in." },
};

/** P11: show the ask only right after a value moment, only when the device could say yes, and not after a recent "Skip reminders". */
export function pushAsk(i: PushAskInput): PushAsk {
  const no = { show: false, title: "", body: "", button: "" };
  if (!i.moment) return no;
  if (i.support === "unsupported" || i.support === "denied" || i.support === "needs_install") return no;
  if (i.support === "granted" && i.registered) return no;
  if (i.dismissedAt && Date.parse(i.now) - Date.parse(i.dismissedAt) < PUSH_ASK_SNOOZE_DAYS * 86_400_000) return no;
  return { show: true, ...ASK_WORDS[i.moment], button: "Turn on reminders" };
}

/**
 * The honest line in Profile for each device state (Safari/iOS included: web push needs iOS 16.4+ and the app added to
 * the Home Screen, webkit.org/blog/13878).
 */
export function pushSupportLine(s: PushSupport, channel: PushChannel): string {
  const where = channel === "phone" ? "this phone" : "this browser";
  switch (s) {
    case "granted": return `Reminders are on for ${where}.`;
    case "default": return `Reminders are off for ${where}. Turn them on below.`;
    case "denied": return channel === "phone" ? "Notifications are blocked for CleanCleaver. Turn them on in your phone's Settings." : "Notifications are blocked for this site. Allow them in your browser's site settings.";
    case "needs_install": return "On iPhone, reminders work after you add CleanCleaver to your Home Screen: tap Share, then Add to Home Screen, then open it from there.";
    case "unsupported": return channel === "phone" ? "This phone can't get reminders from CleanCleaver yet." : "This browser can't get reminders. Try Chrome, Edge, Firefox or Safari.";
  }
}
